import { useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/format";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, Users, FileText, FileCheck2, Landmark,
  Settings, ScrollText, LogOut, Menu, X, BarChart3, FileSearch,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Accueil", icon: LayoutDashboard, exact: true },
  { to: "/assujettis", label: "Assujettis", icon: Users },
  { to: "/notes-debit", label: "Notes de Perception", icon: FileText },
  { to: "/notes-apurement", label: "Notes d'Apurement", icon: FileCheck2 },
  { to: "/rapports", label: "Rapports", icon: BarChart3 },
  { to: "/extrait", label: "Extrait de Compte", icon: FileSearch },
  { to: "/actes", label: "Actes Générateurs", icon: ScrollText },
  { to: "/utilisateurs", label: "Utilisateurs", icon: Landmark, roles: ["admin"] },
  { to: "/parametres", label: "Paramètres", icon: Settings, roles: ["admin"] },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const items = NAV.filter((n) => !n.roles || n.roles.includes(user?.role));

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 transform border-r border-slate-800 bg-slate-900 text-slate-200 transition-transform duration-200 lg:static lg:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="sidebar"
      >
        <div className="flex h-16 items-center gap-3 border-b border-slate-800 px-5">
          <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded bg-white p-1">
            <img src="/logo.png" alt="ONT" className="h-full w-full object-contain" data-testid="sidebar-logo" />
          </div>
          <div className="leading-tight">
            <p className="font-heading text-sm font-bold tracking-wide text-white">ONT — RDC</p>
            <p className="text-[10px] uppercase tracking-[0.14em] text-slate-400">Fonds de Promotion du Tourisme</p>
          </div>
        </div>
        <nav className="flex flex-col gap-1 p-3">
          {items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.exact}
              onClick={() => setOpen(false)}
              data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? "bg-slate-800 text-white"
                    : "text-slate-300 hover:bg-slate-800/60 hover:text-white"
                }`
              }
            >
              <n.icon className="h-[18px] w-[18px]" strokeWidth={1.75} />
              {n.label}
            </NavLink>
          ))}
        </nav>
      </aside>

      {open && (
        <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setOpen(false)} />
      )}

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-16 items-center justify-between border-b border-slate-200 bg-white px-4 lg:px-6">
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden"
              onClick={() => setOpen((o) => !o)}
              data-testid="menu-toggle"
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
            <div>
              <p className="font-heading text-sm font-semibold text-slate-800">
                Office National du Tourisme
              </p>
              <p className="text-[11px] text-slate-500">République Démocratique du Congo</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden text-right sm:block">
              <p className="text-sm font-medium text-slate-800" data-testid="current-user-name">
                {user?.name}
              </p>
              <p className="text-[11px] uppercase tracking-wide text-amber-600">
                {ROLE_LABELS[user?.role] || user?.role}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              data-testid="logout-btn"
              className="text-slate-600 hover:text-red-600"
            >
              <LogOut className="mr-1.5 h-4 w-4" /> Quitter
            </Button>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
